import { Call02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx, formatPhone } from "shared/utils";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";
import {
  callerLabel,
  useMarkNotificationAsRead
} from "../helpers/call-notification";

type CallerNeedsHelpMetadata = Notification["metadata"];

const buildInboxUrl = (metadata: CallerNeedsHelpMetadata): string => {
  if (metadata.conversation_id) return `/inbox?c=${metadata.conversation_id}`;
  if (metadata.lead_id) return `/leads/${metadata.lead_id}`;
  return "/inbox";
};

const dialledLabel = (metadata: CallerNeedsHelpMetadata): string =>
  metadata.to_name?.trim() ||
  (metadata.to_number ? formatPhone(metadata.to_number) : "your number");

export const CallerNeedsHelpNotificationCard = ({
  item
}: {
  item: Notification;
}) => {
  const navigate = useNavigate();
  const { id, read_at, url, metadata, created_at } = item;
  const markAsRead = useMarkNotificationAsRead();
  const destination = url || buildInboxUrl(metadata);

  const open = () => {
    if (!read_at) markAsRead([id]);
  };

  const callerName = callerLabel(metadata);
  const timeLabel = formatCalendar(metadata.occurred_at || created_at);

  return (
    <div className="flex items-stretch gap-6 bg-white p-6 hover:bg-alpha-40">
      <Link
        to={destination}
        onClick={open}
        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-4"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <HugeiconsIcon
            icon={Call02Icon}
            size={16}
            className="text-warning-500 shrink-0"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-content-1000">
            Caller needs help
          </p>
        </div>

        <div className="flex items-center gap-4 min-w-0">
          <span
            className={cx(
              "size-3 rounded-full shrink-0",
              read_at ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          <Avatar name={callerName} size={44} className="font-bold shrink-0" />
          <p className="min-w-0 text-sm text-content-400">
            <span className="font-bold text-content-1000">{callerName}</span>{" "}
            called {dialledLabel(metadata)}. The AI agent couldn’t fully help
            them.
          </p>
        </div>
      </Link>

      <div className="flex shrink-0 flex-col items-end justify-between gap-4">
        <p className="text-content-300 font-medium text-sm">{timeLabel}</p>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={() => {
            open();
            navigate(destination);
          }}
        >
          Open in Inbox
        </Button>
      </div>
    </div>
  );
};
